import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { UserPlus, Heart, Phone, User, CheckCircle2, Loader2, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

import { BACKEND_URL } from '../../config';
const API_BASE = BACKEND_URL;

const RELATIONSHIPS = [
  { id: 'Parent', label: 'Parent (Father / Mother)', emoji: '👨‍👩‍👧' },
  { id: 'Spouse', label: 'Spouse (Wife / Husband)', emoji: '💍' },
  { id: 'Sibling', label: 'Sibling (Brother / Sister)', emoji: '🤝' },
  { id: 'Child', label: 'Child (Son / Daughter)', emoji: '👶' },
  { id: 'Friend', label: 'Close Friend', emoji: '⭐' },
  { id: 'Other', label: 'Guardian / Relative', emoji: '🛡️' },
];

export const AddFamilyMemberModal = ({ isOpen, onClose, onSaved, editMember = null }) => {
  const [name, setName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [relationship, setRelationship] = useState('Parent');
  const [isPrimary, setIsPrimary] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editMember) {
      setName(editMember.name || '');
      setContactNumber(editMember.contact_number || '');
      setRelationship(editMember.relationship || 'Parent');
      setIsPrimary(!!editMember.is_primary);
    } else {
      setName('');
      setContactNumber('');
      setRelationship('Parent');
      setIsPrimary(false);
    }
  }, [editMember, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = contactNumber.replace(/\D/g, '');
    if (!name.trim()) {
      toast({ title: 'Please enter name' });
      return;
    }
    if (cleanPhone.length < 10) {
      toast({ title: 'Please enter valid 10-digit mobile number' });
      return;
    }

    const token = localStorage.getItem('tp_token');
    if (!token) {
      toast({ title: 'Please login first', description: 'Family members are linked with your SSO account.' });
      return;
    }

    setLoading(true);
    try {
      if (editMember) {
        // Update existing member
        await axios.put(
          `${API_BASE}/api/family/members/${editMember.id}`,
          { name, contact_number: cleanPhone, relationship, is_primary: isPrimary },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Family member updated!' });
      } else {
        // Add new member
        await axios.post(
          `${API_BASE}/api/family/members`,
          { name, contact_number: cleanPhone, relationship, is_primary: isPrimary },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Family member added to emergency network!' });
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save family member';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-orange-100 my-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-amber-600 p-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-inner">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight">
                {editMember ? 'Edit Family Contact' : 'Add Family Member'}
              </h3>
              <p className="text-xs text-orange-100">Linked to your SSO account (Max 5)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-orange-600" />
              Full Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Papa / Sharma Ji / Priya"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-orange-600" />
              10-Digit Mobile Number *
            </label>
            <div className="mt-1 flex items-center border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-orange-500">
              <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-300 text-xs font-bold text-gray-600">
                +91
              </span>
              <input
                type="tel"
                required
                maxLength={10}
                placeholder="9876543210"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2.5 text-sm outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <Heart className="w-3.5 h-3.5 text-orange-600" />
              Relationship *
            </label>
            <select
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              className="mt-1 w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-orange-500 outline-none"
            >
              {RELATIONSHIPS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.emoji} {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pt-1">
            <label className="flex items-center gap-2.5 p-3 rounded-xl border border-gray-200 hover:bg-orange-50/50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
              />
              <div>
                <p className="text-xs font-bold text-gray-900">Set as Primary Emergency Contact</p>
                <p className="text-[11px] text-gray-500">First contact to be alerted and called during SOS</p>
              </div>
            </label>
          </div>

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-xl border border-gray-300 hover:bg-gray-100 text-gray-700 font-bold text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 px-4 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md shadow-orange-500/25 transition-all"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : editMember ? 'Save Changes' : 'Add Member'}
            </button>
          </div>
        </form>

      </div>
    </div>,
    document.body
  );
};
